import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Clock, FileText, Plus, RefreshCw, User, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ABSENCE_TYPES = {
  FERIE: { label: 'Ferie', color: 'bg-blue-100 text-blue-800' },
  PERMESSO: { label: 'Permesso', color: 'bg-purple-100 text-purple-800' },
  MALATTIA: { label: 'Malattia', color: 'bg-red-100 text-red-800' },
  INFORTUNIO: { label: 'Infortunio', color: 'bg-orange-100 text-orange-800' },
  PERMESSO_ROL: { label: 'ROL', color: 'bg-green-100 text-green-800' },
  CONGEDO: { label: 'Congedo', color: 'bg-yellow-100 text-yellow-800' }
};

const STATUS_BADGES = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  SUBMITTED: { label: 'Inviata', color: 'bg-blue-100 text-blue-800' },
  APPROVED: { label: 'Approvata', color: 'bg-green-100 text-green-800' },
  REJECTED: { label: 'Rifiutata', color: 'bg-red-100 text-red-800' },
  LOCKED: { label: 'Bloccata', color: 'bg-purple-100 text-purple-800' }
};

export default function MyStaffPortal() {
  const [loading, setLoading] = useState(true);
  const [staffMember, setStaffMember] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { token, selectedCondominium, userRole } = useAuth();
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [absenceForm, setAbsenceForm] = useState({
    type: 'FERIE',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    description: ''
  });
  
  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    check_in_time: '08:00',
    check_out_time: '17:00',
    hours_worked: 8,
    notes: ''
  });

  const condoId = selectedCondominium?.condominium?.id;
  const isAdmin = userRole === 'ADMIN' || selectedCondominium?.role === 'ADMIN';

  const fetchMyData = useCallback(async () => {
    if (!token || !condoId) return;
    setLoading(true);
    try {
      // Get my staff profile
      const profileRes = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setStaffMember(profileData);
        
        // Fetch attendance for selected month
        const attRes = await fetch(
          `${API_URL}/api/condominiums/${condoId}/staff/${profileData.id}/attendance?year=${selectedYear}&month=${selectedMonth}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (attRes.ok) {
          setAttendance(await attRes.json());
        }
        
        // Fetch absences
        const absRes = await fetch(
          `${API_URL}/api/condominiums/${condoId}/staff/${profileData.id}/absences?year=${selectedYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (absRes.ok) {
          const absData = await absRes.json();
          setAbsences(absData.absences || []);
        }
        
        // Fetch documents (buste paga, certificati)
        const docsRes = await fetch(
          `${API_URL}/api/condominiums/${condoId}/staff/${profileData.id}/documents`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (docsRes.ok) {
          const docsData = await docsRes.json();
          setDocuments(docsData.documents || []);
        }
      } else if (profileRes.status === 404) {
        // User is not a staff member - show message
        setStaffMember(null);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token, condoId, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchMyData();
  }, [fetchMyData]);

  const handleSubmitAbsence = async () => {
    if (!staffMember) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMember.id}/absences`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(absenceForm)
        }
      );
      
      if (res.ok) {
        toast.success('Richiesta assenza inviata');
        setAbsenceDialogOpen(false);
        fetchMyData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore invio richiesta');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitAttendance = async () => {
    if (!staffMember) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMember.id}/attendance`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(attendanceForm)
        }
      );
      
      if (res.ok) {
        toast.success('Presenza registrata');
        setAttendanceDialogOpen(false);
        fetchMyData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore registrazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitMonth = async () => {
    if (!staffMember) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/staff/${staffMember.id}/attendance/submit-month`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Presenze inviate per approvazione');
        fetchMyData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore invio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!staffMember) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <User className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Nessun profilo dipendente</h2>
              <p className="text-muted-foreground">
                Il tuo account non è collegato a un profilo dipendente.
                Contatta l'amministratore per l'abilitazione.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <PageHeader
        title="Il Mio Portale"
        subtitle="Area dipendente"
      />

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle>Il Mio Profilo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Nome</Label>
              <p className="font-medium">{staffMember.first_name} {staffMember.last_name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Ruolo</Label>
              <p className="font-medium">{staffMember.role_type}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Tipo Contratto</Label>
              <p className="font-medium">{staffMember.employment_type}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Ore Settimanali</Label>
              <p className="font-medium">{staffMember.weekly_hours}h</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="attendance" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="attendance">
            <Clock className="h-4 w-4 mr-2" />
            Presenze
          </TabsTrigger>
          <TabsTrigger value="absences">
            <Calendar className="h-4 w-4 mr-2" />
            Assenze
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="h-4 w-4 mr-2" />
            Documenti
          </TabsTrigger>
        </TabsList>

        {/* ATTENDANCE TAB */}
        <TabsContent value="attendance">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Presenze Mensili</CardTitle>
                <CardDescription>
                  {attendance?.total_hours || 0} ore totali, {attendance?.total_overtime || 0} straordinari
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>
                        {new Date(2025, m-1, 1).toLocaleDateString('it-IT', {month: 'long'})}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Registra
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Registra Presenza</DialogTitle>
                      <DialogDescription>Inserisci le ore lavorate</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Data</Label>
                        <Input
                          type="date"
                          value={attendanceForm.date}
                          onChange={(e) => setAttendanceForm({...attendanceForm, date: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Entrata</Label>
                          <Input
                            type="time"
                            value={attendanceForm.check_in_time}
                            onChange={(e) => setAttendanceForm({...attendanceForm, check_in_time: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Uscita</Label>
                          <Input
                            type="time"
                            value={attendanceForm.check_out_time}
                            onChange={(e) => setAttendanceForm({...attendanceForm, check_out_time: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Ore Lavorate</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={attendanceForm.hours_worked}
                          onChange={(e) => setAttendanceForm({...attendanceForm, hours_worked: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Note</Label>
                        <Input
                          value={attendanceForm.notes}
                          onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                          placeholder="Note opzionali..."
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAttendanceDialogOpen(false)}>Annulla</Button>
                      <Button onClick={handleSubmitAttendance} disabled={saving}>
                        {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                        Salva
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {attendance?.entries?.length > 0 ? (
                <>
                  <div className="space-y-2 mb-4">
                    {attendance.entries.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-sm">{formatDate(entry.date)}</span>
                          <span className="text-muted-foreground">{entry.check_in_time} - {entry.check_out_time}</span>
                          <Badge variant="outline">{entry.hours_worked}h</Badge>
                        </div>
                        <Badge className={STATUS_BADGES[entry.status]?.color}>
                          {STATUS_BADGES[entry.status]?.label}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  {attendance.status === 'DRAFT' && (
                    <Button onClick={handleSubmitMonth} disabled={saving}>
                      <Send className="h-4 w-4 mr-2" />
                      Invia per Approvazione
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna presenza registrata per questo mese</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABSENCES TAB */}
        <TabsContent value="absences">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Le Mie Assenze</CardTitle>
                <CardDescription>Ferie, permessi, malattie</CardDescription>
              </div>
              <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nuova Richiesta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Richiesta Assenza</DialogTitle>
                    <DialogDescription>Compila i dettagli della richiesta</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select value={absenceForm.type} onValueChange={(v) => setAbsenceForm({...absenceForm, type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ABSENCE_TYPES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Inizio</Label>
                        <Input
                          type="date"
                          value={absenceForm.start_date}
                          onChange={(e) => setAbsenceForm({...absenceForm, start_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Fine</Label>
                        <Input
                          type="date"
                          value={absenceForm.end_date}
                          onChange={(e) => setAbsenceForm({...absenceForm, end_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Textarea
                        value={absenceForm.description}
                        onChange={(e) => setAbsenceForm({...absenceForm, description: e.target.value})}
                        placeholder="Motivo della richiesta..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAbsenceDialogOpen(false)}>Annulla</Button>
                    <Button onClick={handleSubmitAbsence} disabled={saving}>
                      {saving && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                      Invia Richiesta
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {absences.length > 0 ? (
                <div className="space-y-2">
                  {absences.map((absence) => (
                    <div key={absence.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Badge className={ABSENCE_TYPES[absence.type]?.color}>
                          {ABSENCE_TYPES[absence.type]?.label}
                        </Badge>
                        <span className="text-sm">
                          {formatDate(absence.start_date)} - {formatDate(absence.end_date)}
                        </span>
                        {absence.description && (
                          <span className="text-sm text-muted-foreground">{absence.description}</span>
                        )}
                      </div>
                      <Badge className={STATUS_BADGES[absence.status]?.color}>
                        {STATUS_BADGES[absence.status]?.label}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna assenza registrata</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>I Miei Documenti</CardTitle>
              <CardDescription>Buste paga e certificati</CardDescription>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.title}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <a href={`${API_URL}/api/documents/${doc.id}/download`} target="_blank" rel="noreferrer">
                          Scarica
                        </a>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun documento disponibile</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
